import { z } from 'zod'
import * as Sentry from '@sentry/react'
import type { CanvasElement } from '../../domain/canvas/entities/element'
import { errorMessages } from '../shared/errorMessages'

/**
 * Update Element Use Case
 * Handles updating element properties with validation and constraints
 */

// Update command schema
export const UpdateElementCommandSchema = z.object({
  elementId: z.string().min(1),
  updates: z.object({
    position: z.object({ x: z.number(), y: z.number() }).optional(),
    rotation: z.number().optional(),
    dimensions: z.object({ width: z.number(), height: z.number() }).optional(),
    // Text-specific updates
    content: z.string().optional(),
    fontSize: z.number().positive().optional(),
    fontFamily: z.string().optional(),
    fontWeight: z.number().min(100).max(900).optional(),
    fontItalic: z.boolean().optional(),
    color: z
      .string()
      .regex(/^#[0-9A-F]{6}$/i)
      .optional(),
    // Asset-specific updates
    assetColor: z
      .string()
      .regex(/^#[0-9A-F]{6}$/i)
      .optional(),
  }),
})

export type UpdateElementCommand = z.infer<typeof UpdateElementCommandSchema>

// Result type
export type UpdateElementResult = {
  success: boolean
  element?: CanvasElement & { position: { x: number; y: number }; rotation?: number }
  error?: string
}

// Dependencies interface
type UpdateElementDependencies = {
  getElement: (
    elementId: string,
  ) => (CanvasElement & { position: { x: number; y: number }; rotation?: number }) | undefined
  validatePosition?: (
    position: { x: number; y: number },
    element: Pick<CanvasElement, 'type' | 'dimensions'>,
  ) => boolean
  validateSize?: (size: { width: number; height: number }, elementType: string) => boolean
  constrainPosition?: (
    position: { x: number; y: number },
    element: Pick<CanvasElement, 'type' | 'dimensions'>,
  ) => { x: number; y: number }
}

// Factory function to create the use case
export const createUpdateElementUseCase = (deps: UpdateElementDependencies) => ({
  execute: async (command: UpdateElementCommand): Promise<UpdateElementResult> => {
    try {
      // Validate command
      const validatedCommand = UpdateElementCommandSchema.parse(command)

      // Get existing element
      const existingElement = deps.getElement(validatedCommand.elementId)
      if (!existingElement) {
        return {
          success: false,
          error: errorMessages.elementNotFound(validatedCommand.elementId),
        }
      }

      // Start with existing element
      const updatedElement = { ...existingElement }

      // Apply position updates with constraints
      if (validatedCommand.updates.position) {
        let newPosition = validatedCommand.updates.position

        // Apply constraints if available
        if (deps.constrainPosition) {
          newPosition = deps.constrainPosition(newPosition, existingElement)
        }

        // Validate position if validator available
        if (deps.validatePosition && !deps.validatePosition(newPosition, existingElement)) {
          return {
            success: false,
            error: errorMessages.invalidPosition(),
          }
        }

        updatedElement.position = newPosition
      }

      // Apply rotation updates
      if (validatedCommand.updates.rotation !== undefined) {
        updatedElement.rotation = validatedCommand.updates.rotation
        // Also update transform
        updatedElement.transform = {
          ...updatedElement.transform,
          rotation: validatedCommand.updates.rotation,
        }
      }

      // Apply dimension updates
      if (validatedCommand.updates.dimensions) {
        // Validate size if validator available
        if (
          deps.validateSize &&
          !deps.validateSize(validatedCommand.updates.dimensions, existingElement.type)
        ) {
          return {
            success: false,
            error: errorMessages.invalidDimensions(),
          }
        }

        updatedElement.dimensions = validatedCommand.updates.dimensions
      }

      // Apply type-specific updates
      if (existingElement.type === 'text') {
        if (validatedCommand.updates.content !== undefined) {
          ;(updatedElement as any).content = validatedCommand.updates.content
        }
        if (validatedCommand.updates.fontSize !== undefined) {
          ;(updatedElement as any).fontSize = validatedCommand.updates.fontSize
        }
        if (validatedCommand.updates.fontFamily !== undefined) {
          ;(updatedElement as any).fontFamily = validatedCommand.updates.fontFamily
        }
        if (validatedCommand.updates.fontWeight !== undefined) {
          ;(updatedElement as any).fontWeight = validatedCommand.updates.fontWeight
        }
        if (validatedCommand.updates.fontItalic !== undefined) {
          ;(updatedElement as any).fontItalic = validatedCommand.updates.fontItalic
        }
        if (validatedCommand.updates.color !== undefined) {
          ;(updatedElement as any).color = validatedCommand.updates.color
        }
      } else if (existingElement.type === 'asset') {
        if (validatedCommand.updates.assetColor !== undefined) {
          ;(updatedElement as any).color = validatedCommand.updates.assetColor
        }
      }

      // TODO: Emit domain event
      // eventBus.emit(canvasEvents.elementUpdated(updatedElement))

      return {
        success: true,
        element: updatedElement,
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'updateElement' },
        extra: {
          elementId: command.elementId,
          updates: command.updates,
        },
      })
      return {
        success: false,
        error: error instanceof Error ? error.message : errorMessages.failedToUpdateElement(),
      }
    }
  },
})
